import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import React, { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useGetSystemHealthQuery } from "../../api/apiSlice";

// ─── Constants ────────────────────────────────────────────────────────────────

type StatusColor = "success.main" | "warning.main" | "error.main";

const getErrorRateColor = (rate: number): StatusColor => {
  if (rate < 1) return "success.main";
  if (rate < 5) return "warning.main";
  return "error.main";
};

const getResponseTimeColor = (ms: number): StatusColor => {
  if (ms < 200) return "success.main";
  if (ms < 1000) return "warning.main";
  return "error.main";
};

const NOISE_ENDPOINTS = ["/api/health", "/api/ping", "/api/system-health"];

// ─── Sub-components ───────────────────────────────────────────────────────────

interface KpiCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  statusColor?: StatusColor;
}

const KpiCard: React.FC<KpiCardProps> = ({ title, value, subtitle, statusColor }) => (
  <Card sx={{ height: "100%", border: "1px solid", borderColor: "divider" }}>
    <Box sx={{ px: 2, py: 1 }}>
      <Typography variant="body2" color="text.secondary">
        {title}
      </Typography>
    </Box>
    <Divider />
    <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
      <Box display="flex" alignItems="center" gap={1}>
        {statusColor && (
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              bgcolor: statusColor,
              flexShrink: 0,
              ...(statusColor === "error.main" && {
                animation: "pulse 1.5s ease-in-out infinite",
                "@keyframes pulse": {
                  "0%, 100%": { opacity: 1, transform: "scale(1)" },
                  "50%": { opacity: 0.6, transform: "scale(1.3)" },
                },
              }),
            }}
          />
        )}
        <Typography variant="h5" fontWeight={700} color="text.primary">
          {typeof value === "number" ? value.toLocaleString() : value}
        </Typography>
      </Box>
      {subtitle && (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const SectionCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Card sx={{ border: "1px solid", borderColor: "divider" }}>
    <Box sx={{ px: 2, py: 1.5 }}>
      <Typography variant="subtitle2" color="text.primary">
        {title}
      </Typography>
    </Box>
    <Divider />
    <CardContent>{children}</CardContent>
  </Card>
);

// ─── Main Component ───────────────────────────────────────────────────────────

export const SystemHealth: React.FC = () => {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const showDetails =
    process.env.NODE_ENV === "development" || searchParams.get("debug") === "true";
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().subtract(7, "day"));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());

  const queryParams = useMemo(
    () => ({
      startDate: (startDate ?? dayjs().subtract(7, "day")).format("YYYY-MM-DD"),
      endDate: (endDate ?? dayjs()).format("YYYY-MM-DD"),
    }),
    [startDate, endDate],
  );

  const { data: rawData, isLoading, isFetching, isError, refetch } = useGetSystemHealthQuery(queryParams, {
    refetchOnMountOrArgChange: true,
  });

  const data = useMemo(() => {
    if (!rawData) return null;
    return {
      appVersions: rawData.appVersions ?? [],
      errorRate: rawData.errorRate ?? { totalRequests: 0, totalErrors: 0, errorRate: 0, byStatusCode: [], byEndpoint: [] },
      responseTimes: rawData.responseTimes ?? { p50: 0, p95: 0, p99: 0, byEndpoint: [] },
      slowRequests: rawData.slowRequests ?? [],
      daily: rawData.daily ?? [],
    };
  }, [rawData]);

  const filteredResponseEndpoints = useMemo(() => {
    if (!data?.responseTimes.byEndpoint) return [];
    return data.responseTimes.byEndpoint.filter((r) => !NOISE_ENDPOINTS.includes(r.endpoint));
  }, [data]);

  const totalUsers = useMemo(() => {
    if (!data?.appVersions) return 0;
    return data.appVersions.reduce((sum, v) => sum + v.userCount, 0);
  }, [data]);

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ py: 3, opacity: isFetching ? 0.6 : 1, transition: "opacity 0.2s" }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} sx={{ mb: 3 }}>
          <Box display="flex" gap={2} alignItems="center">
            <DatePicker label="Start Date" value={startDate} onChange={setStartDate} format="YYYY-MM-DD" slotProps={{ textField: { size: "small" } }} />
            <DatePicker label="End Date" value={endDate} onChange={setEndDate} format="YYYY-MM-DD" slotProps={{ textField: { size: "small" } }} />
          </Box>
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} disabled={isFetching} size="small" aria-label="Refresh system health data">
              <RefreshIcon sx={{ animation: isFetching ? "spin 1s linear infinite" : "none", "@keyframes spin": { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } } }} />
            </IconButton>
          </Tooltip>
        </Box>

        {isError || !data ? (
          <Typography color="text.secondary">No data available.</Typography>
        ) : (
          <Grid container spacing={3}>
            {/* ─── KPI Cards ───────────────────────────────────────── */}
            {showDetails && (
            <Grid size={{ xs: 12 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <KpiCard
                    title="Error Rate"
                    value={`${data.errorRate.errorRate.toFixed(2)}%`}
                    subtitle={`${data.errorRate.totalErrors} / ${data.errorRate.totalRequests} requests`}
                    statusColor={getErrorRateColor(data.errorRate.errorRate)}
                  />
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <KpiCard
                    title="Typical User Experience (p50)"
                    value={`${data.responseTimes.p50.toFixed(0)} ms`}
                    subtitle="Half of all users wait this long or less"
                    statusColor={getResponseTimeColor(data.responseTimes.p50)}
                  />
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <KpiCard
                    title="Slowest 5% Experience (p95)"
                    value={`${data.responseTimes.p95.toFixed(0)} ms`}
                    subtitle="95% of users get a response under this time"
                    statusColor={getResponseTimeColor(data.responseTimes.p95)}
                  />
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <KpiCard
                    title="Worst-Case Experience (p99)"
                    value={`${data.responseTimes.p99.toFixed(0)} ms`}
                    subtitle="99% of requests finish under this time"
                    statusColor={getResponseTimeColor(data.responseTimes.p99)}
                  />
                </Grid>
              </Grid>
            </Grid>
            )}

            {/* ─── Daily Trends: Traffic Volume ───────────────────── */}
            {showDetails && data.daily.length > 0 && (
              <Grid size={{ xs: 12 }}>
                <SectionCard title="Traffic Volume">
                  <Box sx={{ height: 280 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.daily} margin={{ left: 10, right: 20, top: 10, bottom: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <RechartsTooltip formatter={(value) => Number(value).toLocaleString()} />
                        <Legend />
                        <Bar dataKey="totalRequests" name="Requests" fill={theme.palette.primary.main} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </SectionCard>
              </Grid>
            )}

            {/* ─── Daily Trends: Response Times ────────────────────── */}
            {showDetails && data.daily.length > 0 && (
              <Grid size={{ xs: 12, md: 6 }}>
                <SectionCard title="Response Times Over Time">
                  <Box sx={{ height: 280 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={data.daily} margin={{ left: 10, right: 20, top: 10, bottom: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                        <YAxis unit=" ms" tick={{ fontSize: 11 }} />
                        <RechartsTooltip formatter={(value) => `${value} ms`} />
                        <Legend />
                        <Area type="monotone" dataKey="p50" name="p50 (Median)" stroke="#10b981" fill="#10b98133" strokeWidth={2} />
                        <Area type="monotone" dataKey="p95" name="p95 (Tail)" stroke="#f59e0b" fill="#f59e0b33" strokeWidth={2} />
                        <Area type="monotone" dataKey="p99" name="p99 (Worst)" stroke="#ef4444" fill="#ef444433" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </Box>
                </SectionCard>
              </Grid>
            )}

            {/* ─── Daily Trends: Error Rate ────────────────────────── */}
            {showDetails && data.daily.length > 0 && (
              <Grid size={{ xs: 12, md: 6 }}>
                <SectionCard title="Error Rate Over Time">
                  <Box sx={{ height: 280 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={data.daily} margin={{ left: 10, right: 20, top: 10, bottom: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                        <YAxis unit="%" tick={{ fontSize: 11 }} />
                        <RechartsTooltip formatter={(value) => `${Number(value).toFixed(2)}%`} />
                        <Area type="monotone" dataKey="errorRate" name="Error Rate" stroke="#ef4444" fill="#ef444433" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </Box>
                </SectionCard>
              </Grid>
            )}

            {/* ─── App Versions ─────────────────────────────────────── */}
            <Grid size={{ xs: 12 }}>
              <SectionCard title="App Versions">
                <Box sx={{ height: 280, mb: 2 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.appVersions} layout="vertical" margin={{ left: 60, right: 20, top: 10, bottom: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis type="category" dataKey="version" width={80} tick={{ fontSize: 12 }} />
                      <RechartsTooltip />
                      <Bar dataKey="userCount" name="Users" fill={theme.palette.primary.main} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Version</TableCell>
                        <TableCell>Platform</TableCell>
                        <TableCell align="right">Users</TableCell>
                        <TableCell align="right">% of Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.appVersions.map((row, i) => (
                        <TableRow key={i}>
                          <TableCell>{row.version}</TableCell>
                          <TableCell>{row.platform ?? "—"}</TableCell>
                          <TableCell align="right">{row.userCount.toLocaleString()}</TableCell>
                          <TableCell align="right">{totalUsers > 0 ? ((row.userCount / totalUsers) * 100).toFixed(1) : 0}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </SectionCard>
            </Grid>

            {/* ─── Error Rate by Endpoint ──────────────────────────── */}
            {showDetails && (
            <Grid size={{ xs: 12 }}>
              <SectionCard title="Errors by Endpoint">
                {data.errorRate.byEndpoint.length === 0 ? (
                  <Typography color="text.secondary" variant="body2">No errors recorded.</Typography>
                ) : (
                  <TableContainer sx={{ maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Method</TableCell>
                          <TableCell>Endpoint</TableCell>
                          <TableCell>Original URL</TableCell>
                          <TableCell align="right">Status</TableCell>
                          <TableCell align="right">Count</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {[...data.errorRate.byEndpoint].sort((a, b) => b.count - a.count).map((row, i) => (
                          <TableRow key={i}>
                            <TableCell><Typography variant="body2" fontFamily="monospace">{row.method}</Typography></TableCell>
                            <TableCell><Typography variant="body2" fontFamily="monospace">{row.endpoint}</Typography></TableCell>
                            <TableCell><Typography variant="body2" fontFamily="monospace" color="text.secondary">{row.originalUrl ?? "—"}</Typography></TableCell>
                            <TableCell align="right">{row.statusCode}</TableCell>
                            <TableCell align="right">{row.count.toLocaleString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </SectionCard>
            </Grid>
            )}

            {/* ─── Response Times by Endpoint ──────────────────────── */}
            {showDetails && (
            <Grid size={{ xs: 12 }}>
              <SectionCard title="Response Times by Endpoint">
                {filteredResponseEndpoints.length === 0 ? (
                  <Typography color="text.secondary" variant="body2">No data available.</Typography>
                ) : (
                  <TableContainer sx={{ maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Method</TableCell>
                          <TableCell>Endpoint</TableCell>
                          <TableCell align="right">Avg (ms)</TableCell>
                          <TableCell align="right">Max (ms)</TableCell>
                          <TableCell align="right">Requests</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {[...filteredResponseEndpoints].sort((a, b) => b.avg - a.avg).map((row, i) => (
                          <TableRow key={i}>
                            <TableCell><Typography variant="body2" fontFamily="monospace">{row.method}</Typography></TableCell>
                            <TableCell><Typography variant="body2" fontFamily="monospace">{row.endpoint}</Typography></TableCell>
                            <TableCell align="right">{row.avg.toFixed(0)}</TableCell>
                            <TableCell align="right">{row.max.toFixed(0)}</TableCell>
                            <TableCell align="right">{row.count.toLocaleString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </SectionCard>
            </Grid>
            )}

            {/* ─── Slow Requests ───────────────────────────────────── */}
            {showDetails && (
            <Grid size={{ xs: 12 }}>
              <SectionCard title="Slow Requests (> 2s)">
                {data.slowRequests.length === 0 ? (
                  <Typography color="text.secondary" variant="body2">No slow requests recorded.</Typography>
                ) : (
                  <TableContainer sx={{ maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Method</TableCell>
                          <TableCell>Endpoint</TableCell>
                          <TableCell align="right">Status</TableCell>
                          <TableCell align="right">Time (ms)</TableCell>
                          <TableCell>Timestamp</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.slowRequests.filter((r) => r.endpoint !== "/api/system-health").map((row, i) => (
                          <TableRow key={i}>
                            <TableCell><Typography variant="body2" fontFamily="monospace">{row.method}</Typography></TableCell>
                            <TableCell><Typography variant="body2" fontFamily="monospace">{row.endpoint}</Typography></TableCell>
                            <TableCell align="right">{row.statusCode}</TableCell>
                            <TableCell align="right">{row.responseTimeMs.toLocaleString()}</TableCell>
                            <TableCell>{dayjs(row.createdAt).format("YYYY-MM-DD HH:mm:ss")}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </SectionCard>
            </Grid>
            )}
          </Grid>
        )}
      </Box>
    </LocalizationProvider>
  );
};
